<template>
  <div
    class="h-[50px] px-4 py-2.5 bg-fill-brand-secondary rounded-2xl items-center inline-flex cursor-pointer"
    :class="{
      'flex-row-reverse justify-end gap-2': previewing,
      'justify-between': !previewing,
    }"
    @click="$emit('click')"
  >
    <div class="flex items-center gap-2">
      <span
        class="text-center text-white text-base font-normal font-serif uppercase leading-tight"
      >
        {{ displayTitle }}
      </span>
      <span
        v-if="isNext || isCurrent"
        class="h-7 px-3 py-1.5 rounded-[23px] justify-center items-center gap-2.5 inline-flex text-center text-white text-[13px] font-medium font-sans"
        :class="{
          'bg-fill-warning-secondary': isNext,
          'bg-fill-dark': isCurrent,
        }"
      >
        {{ isNext ? "Next" : "Current" }}
        {{ type === LeaderboardPrizeType.Weekly ? "week" : "month" }}
      </span>
      <span
        v-if="!enabled"
        class="text-text-disabled text-sm font-normal font-serif uppercase leading-tight"
      >
        [DRAFT]
      </span>
    </div>
    <div class="relative">
      <Icon
        :icon="previewing ? 'mdi:chevron-left' : 'mdi:chevron-right'"
        class="text-white size-6"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Icon } from "@iconify/vue";
import { computed } from "vue";
import { LeaderboardPrizeType } from "@/generated/graphql";
import { addMonths, addWeeks, isAfter, isBefore } from "@/utils/date";
import { startOfUTCWeek, startOfUTCMonth } from "@/utils/date";

const props = defineProps<{
  date: Date;
  type: LeaderboardPrizeType;
  previewing?: boolean;
  enabled?: boolean;
}>();

defineEmits<{
  click: [];
}>();

const dateUtc = computed(() => {
  return new Date(
    Date.UTC(
      props.date.getUTCFullYear(),
      props.date.getUTCMonth(),
      props.date.getUTCDate(),
    ),
  );
});

const displayTitle = computed(() => {
  if (!props.type || !props.date) {
    return "";
  }

  const _utcDate = new Date(
    Date.UTC(
      props.date.getUTCFullYear(),
      props.date.getUTCMonth(),
      props.date.getUTCDate(),
    ),
  );

  if (props.type === LeaderboardPrizeType.Monthly) {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: "UTC",
      month: "long",
      year: "numeric",
    });
    return formatter.format(props.date).replace(" ", ", ");
  }

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  return formatter.format(props.date);
});

const isNext = computed(() => {
  const now = new Date();
  const currentPeriodStart = getPeriodStart(now, props.type);
  const nextPeriodStart = getNextPeriodStart(currentPeriodStart, props.type);

  return (
    isAfter(dateUtc.value, nextPeriodStart) ||
    dateUtc.value.getTime() === nextPeriodStart.getTime()
  );
});

const isCurrent = computed(() => {
  const now = new Date();
  const currentPeriodStart = getPeriodStart(now, props.type);
  const nextPeriodStart = getNextPeriodStart(currentPeriodStart, props.type);

  return (
    (isAfter(dateUtc.value, currentPeriodStart) ||
      dateUtc.value.getTime() === currentPeriodStart.getTime()) &&
    isBefore(dateUtc.value, nextPeriodStart)
  );
});

function getPeriodStart(date: Date, type: LeaderboardPrizeType): Date {
  if (type === LeaderboardPrizeType.Weekly) {
    return startOfUTCWeek(date, 1);
  } else {
    return startOfUTCMonth(date);
  }
}

function getNextPeriodStart(
  currentPeriodStart: Date,
  type: LeaderboardPrizeType,
): Date {
  if (type === LeaderboardPrizeType.Weekly) {
    return addWeeks(currentPeriodStart, 1);
  } else {
    return addMonths(currentPeriodStart, 1);
  }
}
</script>
